import React, { useState, useMemo } from "react";
import AppDataGrid from "../../common/AppDataGrid";
import {
  CircularProgress,
  Box,
  Container,
  Snackbar,
  Alert,
} from "@mui/material";
import useFirestore from "../../../config/fireBaseHook";
import Add from "./inc/Add";
import Edit from "./inc/Edit";
import EditUpload from "../../../hooks/EditUpload";
import ClearCollectionButton from "../../common/ClearCollectionButton";
import TechPrepPortalMgtBreadCrumbs from "../../Layout/TechPrepPortalMgtBreadCrumbs";
import { useAuth } from "../../../config/AuthContext";
import CustomFilterPanel from "../../common/CustomFilterPanel";
import useCustomFilter from "../../../hooks/useCustomFilter";
import { pathwayProgramsFilters } from "../filterConfigurations";
import SimpleToolbar from "../../common/SimpleToolbar";

const PathwayPrograms = () => {
  const { userPermissions } = useAuth();
  const canEdit = userPermissions["Pathway Programs"]?.edit;
  const {
    data: pathwayPrograms,
    loading,
    error,
  } = useFirestore("PathwaysPrograms", "PathwayName", "asc");
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [sortModel, setSortModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0,
  });
  const pageName = "Pathway Programs";

  // Use custom filter hook
  const {
    filteredData,
    filterModel,
    setFilterModel,
    resetFilters,
    hasActiveFilters,
    isFiltering,
  } = useCustomFilter(pathwayPrograms || [], pathwayProgramsFilters);

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilterModel(newFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const columns = useMemo(
    () => [
      {
        field: "PathwayName",
        headerName: "Pathway Name",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "PathwayNameShort",
        headerName: "Pathway Name Short",
        flex: 1,
        minWidth: 140,
      },
      {
        field: "PathwayAbbrev",
        headerName: "Pathway Abbreviation",
        flex: 1,
        minWidth: 130,
      },
      {
        field: "Career_Field_Cd",
        headerName: "Career Field Code",
        flex: 1,
        minWidth: 130,
      },
      {
        field: "DivisionID",
        headerName: "Division ID",
        flex: 1,
        minWidth: 110,
      },
      {
        field: "SCCDirector",
        headerName: "SCC Director",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "SCCProgramMgr",
        headerName: "SCC Program Manager",
        flex: 1,
        minWidth: 160,
      },
      { field: "SCCLiason", headerName: "SCC Liason", flex: 1, minWidth: 120 },
      {
        field: "HEIProgramName",
        headerName: "HEI Program Name",
        flex: 1,
        minWidth: 150,
      },
      { field: "Notes", headerName: "Notes", flex: 1, minWidth: 100 },
      { field: "Start_Date", headerName: "Start Date", flex: 1, minWidth: 110 },
      { field: "End_Date", headerName: "End Date", flex: 1, minWidth: 110 },
    ],
    [],
  );

  // Prepare data for DataGrid
  const rows = useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData)) return [];
    return filteredData.map((program, index) => ({
      id: program.id || index, // Use program.id if available, otherwise use index
      ...program,
    }));
  }, [filteredData]);

  const templateFields = Array.from(
    new Set([...columns.map((col) => col.field), "PathwayID"]),
  );

  const handleRowClick = (params) => {
    if (canEdit) {
      setSelectedProgram(params.row);
      setEditDialogOpen(true);
    }
  };

  const handleDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedProgram(null);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleRecordUpdated = () => {
    setSnackbarMessage("Record has been updated successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    setEditDialogOpen(false);
  };

  const handleRecordAdded = () => {
    setSnackbarMessage("Record has been added successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleBulkActionComplete = (success, message) => {
    setSnackbarMessage(
      message ||
        (success ? "Bulk operation successful." : "Bulk operation failed."),
    );
    setSnackbarSeverity(success ? "success" : "error");
    setSnackbarOpen(true);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <p>
          Error fetching pathway programs:{" "}
          {typeof error === "string"
            ? error
            : error?.message || "Unknown error"}
        </p>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ height: "100vh", paddingTop: "50px" }}>
      <div style={{ height: "100%", width: "100%" }}>
        <TechPrepPortalMgtBreadCrumbs pageName={pageName} icon="faCog" />

        {/* Custom Filter Panel */}
        <CustomFilterPanel
          filterConfig={pathwayProgramsFilters}
          onFiltersChange={handleFiltersChange}
          initialFilters={{}}
          title="Pathway Programs Filters"
          resultCount={filteredData.length}
          totalCount={pathwayPrograms?.length || 0}
          isSearching={isFiltering}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          {canEdit && <Add onRecordAdded={handleRecordAdded} />}
          {canEdit && (
            <EditUpload
              collectionName="PathwaysPrograms"
              onActionComplete={handleBulkActionComplete}
              exportColumnOrder={columns.map((col) => col.field)}
              exportData={pathwayPrograms}
              templateFields={templateFields}
            />
          )}
          {canEdit && (
            <ClearCollectionButton collectionName="PathwaysPrograms" />
          )}
        </Box>

        <Box sx={{ height: "70vh", width: "100%" }}>
          <AppDataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            slots={{ toolbar: SimpleToolbar }}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 100, 200]}
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            sx={{
              "& .MuiDataGrid-row:hover": {
                cursor: canEdit ? "pointer" : "default",
              },
            }}
          />
        </Box>
        {/* Pagination Controls */}
        {/* (Removed bottom pagination controls) */}
      </div>
      {selectedProgram && canEdit && (
        <Edit
          open={editDialogOpen}
          onClose={handleDialogClose}
          programDetail={selectedProgram}
          onRecordUpdated={handleRecordUpdated}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PathwayPrograms;
